// PLAN-cockpit-v2 Phase 0-C: 共通 Detail panel (Header + Body + Footer)
//
// Lens 別の context section は children として注入する。
// 例: Explore は version chain navigator + related entries / Graph は in/out degree + cluster name 等

import { useRef, useState, type ReactNode } from 'react'
import { Markdown } from './Markdown'
import { MetaBlock } from './MetaBlock'
import { RefList } from './RefList'

interface DomainBadgeRow {
  domain: string
  provenance: 'declared' | 'curated' | 'inferred'
  polarity?: 'include' | 'exclude'
  confidence?: number | null
  source?: string | null
  reason?: string | null
}

interface DetailProps {
  title: string
  body?: string
  one_line?: string | null
  key_points?: string[]   // PLAN-domain-classifier-iter2 (2026-05-11): 要点 box
  origin?: string | null
  subjectivity?: string | null
  visibility?: string | null
  lifecycle_stage?: string | null
  source?: { tool?: string | null; medium?: string | null; project?: string | null } | null
  source_actor?: string | null
  source_context?: string | null
  declared_domains?: string[]
  curated?: DomainBadgeRow[]
  inferred?: DomainBadgeRow[]
  created_at?: string | null
  updated_at?: string | null
  highlightTerms?: string[]
  // PLAN-domain-classifier-iter2 (2026-05-11): Article 評価 / メタ表示 + 統合元 / 派生元 / 前バージョン
  article_meta?: {
    theme?: string
    version_label?: string | null
    confidence?: string
    applicability?: string
    confidence_basis?: string | null
  }
  source_entry_ids?: string[]
  source_article_ids?: string[]
  previous_version?: string | null
  // PVW Core Decision Payload (PLAN-pvw-dsl-adoption, 2026-05-13)
  decision?: PvwDecisionPayload | null
  migration_status?: 'pending' | 'migrated' | 'migrated_with_warnings' | 'quarantined' | 'rejected' | null
  text_schema_status?: 'primary_axis' | 'context_only' | 'weak_context' | 'invalid_text_schema' | 'not_article' | null
  has_decision?: boolean | number | null
  x_status?: 'present' | 'all_rejected' | 'not_applicable' | null
  decision_size_class?: 'normal' | 'large' | null
  /** Lens 別の追加 section (Explore: version chain / Graph: degree / Curate: editor 等) */
  contextSection?: ReactNode
  /** 上部の actions (PermalinkButton 等) */
  headerActions?: ReactNode
  onClose?: () => void
  // PLAN-permalink-cockpit-convergence: permalink view 用。RefList の自前 fetch を回避する。
  /** 解決済み参照 (id 横断の global map)。3 つの RefList に渡される */
  resolvedRefs?: Record<string, { title: string; origin?: string }>
  /** RefList の title click 遷移。permalink view は /v/{id} 遷移を渡す */
  onRefOpen?: (id: string, itemType: 'entry' | 'article') => void
}

export interface PvwDecisionPayload {
  schema_version?: string
  x_status?: 'present' | 'all_rejected' | 'not_applicable'
  x?: {
    candidates?: Array<{ id: string; statement?: string; reason?: string; rejected?: boolean; rejection_reason?: string; source_refs?: string[] }>
    selected_ids?: string[]
  }
  c_audit?: {
    principles_consulted?: string[]
    rules_applied?: string[]
    consistency_checks?: Array<{ rule: string; result: string }>
  }
  decision_trace?: {
    summary?: string
    steps?: string[]
    parameter_recommendations?: Record<string, unknown>
    open_questions?: string[]
  }
}

export function Detail(props: DetailProps) {
  const {
    title, body, one_line, key_points = [],
    article_meta, source_entry_ids = [], source_article_ids = [], previous_version,
    decision,
    contextSection, headerActions, onClose, highlightTerms = [],
    resolvedRefs, onRefOpen,
    ...meta
  } = props
  // mobile: スクロール位置に応じてヘッダーのフットプリントを可変。
  // 最上部のみ少し大きく (情報量増)、スクロール後はコンパクト。desktop は影響なし。
  const [scrolled, setScrolled] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  function onBodyScroll() {
    const top = scrollRef.current?.scrollTop ?? 0
    setScrolled(prev => (prev ? top > 4 : top > 24))
  }

  const isArticle = props.origin === 'synthesized'
  const hasFooter = isArticle && (
    article_meta?.confidence || article_meta?.applicability ||
    source_entry_ids.length > 0 || source_article_ids.length > 0 || previous_version
  )

  return (
    <div className="h-full flex flex-col">
      {/* Header — mobile はフットプリント最小化 + スクロール連動で可変 (最上部のみ少し大きく) */}
      <header
        className={`flex-shrink-0 border-b border-border-subtle bg-bg-surface transition-all duration-150 sm:!px-6 sm:!pt-5 sm:!pb-4 ${
          scrolled ? 'px-3 pt-1.5 pb-1.5' : 'px-3 pt-2.5 pb-2.5'
        }`}
      >
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <span className={`text-[10px] uppercase tracking-wide font-medium ${isArticle ? 'text-accent-secondary' : 'text-text-subtle'}`}>
              {isArticle ? 'Article' : 'Entry'}
              {props.lifecycle_stage && props.lifecycle_stage !== 'active' && (
                <span className="ml-2 text-text-subtle">· {props.lifecycle_stage}</span>
              )}
            </span>
            <h2 className={`text-sm sm:text-md font-medium text-text-base mt-0.5 sm:mt-1 leading-snug ${scrolled ? 'line-clamp-1' : 'line-clamp-3'} sm:!line-clamp-2`}>{title}</h2>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1 sm:gap-2">
            {headerActions}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close detail"
                className="text-text-subtle hover:text-text-base text-lg leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-bg-overlay"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Body + Meta + Context section */}
      <div ref={scrollRef} onScroll={onBodyScroll} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <MetaBlock {...meta} />

        {/* one_line (abstract) */}
        {one_line && (
          <p className="text-sm italic text-text-muted border-l-2 border-border-subtle pl-3">
            {one_line}
          </p>
        )}

        {/* 要点 box (PLAN-domain-classifier-iter2 2026-05-11): Article の key_points を amber 系のハイライト box で表示 */}
        {key_points.length > 0 && (
          <section
            className="rounded p-4 border"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--state-warning) 8%, transparent)',
              borderColor: 'color-mix(in srgb, var(--state-warning) 30%, transparent)',
            }}
          >
            <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-2">要点</h3>
            <ul className="text-sm text-text-base list-disc list-inside space-y-1">
              {key_points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </section>
        )}

        {body && (
          <section>
            <Markdown source={body} highlightTerms={highlightTerms} />
          </section>
        )}

        {/* PVW Core Decision Payload — Article 本文の直後 (decision is the substance, not a footnote) */}
        {decision && <DecisionSection decision={decision} sizeClass={props.decision_size_class ?? null} />}

        {contextSection && (
          <section className="border-t border-border-subtle pt-5">
            {contextSection}
          </section>
        )}

        {/* Article footer: 評価 + メタ + 統合元 + 派生元 + 前バージョン */}
        {hasFooter && (
          <div className="border-t border-border-subtle pt-5 space-y-5">
            {(article_meta?.confidence || article_meta?.applicability) && (
              <section className="text-sm space-y-2">
                <h3 className="text-xs uppercase tracking-wide text-text-subtle">評価</h3>
                <dl className="grid grid-cols-[100px_1fr] gap-y-1 gap-x-3 text-text-base">
                  {article_meta?.confidence && (
                    <>
                      <dt className="text-text-subtle">confidence</dt>
                      <dd>{article_meta.confidence}</dd>
                    </>
                  )}
                  {article_meta?.applicability && (
                    <>
                      <dt className="text-text-subtle">applicability</dt>
                      <dd>{article_meta.applicability}</dd>
                    </>
                  )}
                  {article_meta?.confidence_basis && (
                    <>
                      <dt className="text-text-subtle">basis</dt>
                      <dd>{article_meta.confidence_basis}</dd>
                    </>
                  )}
                </dl>
              </section>
            )}
            {(article_meta?.theme || article_meta?.version_label) && (
              <section className="text-sm space-y-2">
                <h3 className="text-xs uppercase tracking-wide text-text-subtle">メタ</h3>
                <dl className="grid grid-cols-[100px_1fr] gap-y-1 gap-x-3 text-text-base">
                  {article_meta?.theme && (
                    <>
                      <dt className="text-text-subtle">theme</dt>
                      <dd>{article_meta.theme}</dd>
                    </>
                  )}
                  {article_meta?.version_label && (
                    <>
                      <dt className="text-text-subtle">version</dt>
                      <dd>{article_meta.version_label}</dd>
                    </>
                  )}
                </dl>
              </section>
            )}
            {source_entry_ids.length > 0 && (
              <RefList ids={source_entry_ids} itemType="entry" label={`統合元 Entry (${source_entry_ids.length})`} resolvedRefs={resolvedRefs} onOpen={onRefOpen} />
            )}
            {source_article_ids.length > 0 && (
              <RefList ids={source_article_ids} itemType="article" label={`派生元 Article (${source_article_ids.length})`} resolvedRefs={resolvedRefs} onOpen={onRefOpen} />
            )}
            {previous_version && (
              <RefList ids={[previous_version]} itemType="article" label="前バージョン" resolvedRefs={resolvedRefs} onOpen={onRefOpen} />
            )}
          </div>
        )}

      </div>
    </div>
  )
}

function DecisionSection({ decision, sizeClass }: { decision: PvwDecisionPayload; sizeClass: 'normal' | 'large' | null }) {
  const selectedIds = new Set(decision.x?.selected_ids ?? [])
  const candidates = decision.x?.candidates ?? []
  const xStatus = decision.x_status ?? 'present'
  const summaryLine = decision.decision_trace?.summary
  return (
    <details
      className="rounded border"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--accent-secondary) 6%, transparent)',
        borderColor: 'color-mix(in srgb, var(--accent-secondary) 35%, transparent)',
      }}
    >
      <summary className="cursor-pointer select-none px-4 py-3 list-none">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wide font-medium text-accent-secondary">◇ Decision</span>
          <span
            className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
              xStatus === 'present' ? 'text-emerald-600' : 'text-text-subtle'
            }`}
            style={{
              backgroundColor: xStatus === 'present'
                ? 'color-mix(in srgb, var(--state-success, #10b981) 14%, transparent)'
                : 'color-mix(in srgb, var(--text-subtle) 12%, transparent)',
            }}
          >
            {xStatus}
          </span>
          <span className="text-xs text-text-muted">
            {candidates.length} candidates · {selectedIds.size} selected
          </span>
          {sizeClass === 'large' && <span className="text-[10px] text-amber-500">· large</span>}
        </div>
        {summaryLine && (
          <p className="text-sm text-text-base mt-1.5 line-clamp-2">{summaryLine}</p>
        )}
      </summary>
      <div className="space-y-5 px-4 pb-4 pt-1">
        {decision.decision_trace?.summary && (
          <section className="text-sm">
            <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Summary</h3>
            <p className="text-text-base">{decision.decision_trace.summary}</p>
          </section>
        )}
        {candidates.length > 0 && (
          <section className="text-sm space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-text-subtle">X candidates ({candidates.length})</h3>
            <ul className="space-y-2">
              {candidates.map((cand) => {
                const isSelected = selectedIds.has(cand.id)
                return (
                  <li
                    key={cand.id}
                    className={`text-xs rounded p-2 border ${isSelected ? 'border-accent-secondary bg-accent-secondary/5' : 'border-border-subtle bg-bg-overlay/30'}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-text-subtle">{cand.id}</span>
                      {isSelected && <span className="text-accent-secondary text-[10px] font-medium uppercase">selected</span>}
                      {cand.rejected && <span className="text-text-subtle text-[10px] uppercase">rejected</span>}
                    </div>
                    {cand.statement && <div className="text-text-base mt-1">{cand.statement}</div>}
                    {cand.reason && <div className="text-text-muted mt-1 italic">理由: {cand.reason}</div>}
                    {cand.rejected && cand.rejection_reason && (
                      <div className="text-amber-600 mt-1">却下: {cand.rejection_reason}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )}
        {decision.c_audit && (decision.c_audit.principles_consulted?.length || decision.c_audit.rules_applied?.length || decision.c_audit.consistency_checks?.length) ? (
          <section className="text-sm space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-text-subtle">C audit</h3>
            {decision.c_audit.principles_consulted && decision.c_audit.principles_consulted.length > 0 && (
              <div className="text-xs"><span className="text-text-subtle">principles: </span><span className="text-text-base">{decision.c_audit.principles_consulted.join(', ')}</span></div>
            )}
            {decision.c_audit.rules_applied && decision.c_audit.rules_applied.length > 0 && (
              <div className="text-xs"><span className="text-text-subtle">rules: </span><span className="text-text-base">{decision.c_audit.rules_applied.join(', ')}</span></div>
            )}
            {decision.c_audit.consistency_checks && decision.c_audit.consistency_checks.length > 0 && (
              <ul className="text-xs text-text-base space-y-0.5">
                {decision.c_audit.consistency_checks.map((c, i) => (
                  <li key={i}>
                    <span className="font-mono text-text-subtle">{c.rule}</span> →{' '}
                    <span className={c.result === 'pass' ? 'text-emerald-500' : 'text-amber-500'}>{c.result}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
        {decision.decision_trace && (decision.decision_trace.steps?.length || decision.decision_trace.open_questions?.length || (decision.decision_trace.parameter_recommendations && Object.keys(decision.decision_trace.parameter_recommendations).length > 0)) ? (
          <section className="text-sm space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-text-subtle">Trace</h3>
            {decision.decision_trace.steps && decision.decision_trace.steps.length > 0 && (
              <ol className="text-xs text-text-base list-decimal list-inside space-y-0.5">
                {decision.decision_trace.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            )}
            {decision.decision_trace.parameter_recommendations && Object.keys(decision.decision_trace.parameter_recommendations).length > 0 && (
              <div className="text-xs">
                <div className="text-text-subtle mb-1">Parameter recommendations:</div>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-text-base">
                  {Object.entries(decision.decision_trace.parameter_recommendations).map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="font-mono text-text-subtle">{k}</dt>
                      <dd className="break-words">
                        {typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
                          ? String(v)
                          : JSON.stringify(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {decision.decision_trace.open_questions && decision.decision_trace.open_questions.length > 0 && (
              <div className="text-xs">
                <div className="text-text-subtle mb-1">Open questions:</div>
                <ul className="text-text-base list-disc list-inside space-y-0.5">
                  {decision.decision_trace.open_questions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </details>
  )
}
